import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Bot Followers Audit arc.
 *
 * Triggered when followers >= 100K. A journalist publishes that 87% of
 * your followers are bots. 3 PR decisions decide whether you get caught
 * cleansing, pivot, or own it.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'A NEW YORKER REPORTER: "we ran an audit. 87% of your followers are bots. care to comment before we publish?"',
    options: [
      { label: '"that\'s a flattering ratio."', effect: () => {
        usePower.getState().bumpRep('press', 8);
        usePower.getState().bumpRep('wsb', 14);
        usePower.getState().bumpNotoriety(8);
      } },
      { label: '"we\'ll cleanse the bots tonight."', effect: () => {
        {
          const cur = useXClone.getState().followers;
          useXClone.getState().bumpFollowers(-Math.floor(cur * 0.87));
        }
        usePower.getState().bumpRep('press', -6);
        useLife.getState().bumpSanity(-5);
      } },
      { label: '"we are aware of the report. we\'ve no further comment."', effect: () => {
        usePower.getState().bumpNotoriety(-3);
      } },
    ],
  },
  {
    text: 'YOUR PR FLACK: "story drops in 6 hours. damage control?"',
    options: [
      { label: 'launch a "real followers" campaign — \$25K to amplify', effect: () => {
        useXClone.getState().bumpFollowers(4_000);
        usePower.getState().bumpRep('press', 4);
      } },
      { label: 'pre-empt with a self-deprecating Substack post', effect: () => {
        usePower.getState().bumpRep('press', 8);
        usePower.getState().bumpRep('wsb', 6);
      } },
      { label: 'sue the New Yorker', effect: () => {
        usePower.getState().bumpRep('press', -12);
        usePower.getState().bumpRep('maga', 8);
      } },
    ],
  },
  {
    text: 'TWITTER DM: "we can sell you 50K \"real-looking\" accounts for \$8K. your call."',
    options: [
      { label: 'buy them. take the L on a future second audit.', effect: () => {
        useXClone.getState().bumpFollowers(50_000);
        useLife.getState().bumpSanity(-3);
      } },
      { label: 'pass. let the count drop naturally.', effect: () => {
        usePower.getState().bumpRep('press', 4);
      } },
      { label: 'forward the DM to Bloomberg', effect: () => {
        usePower.getState().bumpRep('press', 12);
        usePower.getState().bumpNotoriety(6);
      } },
    ],
  },
];

export function startBotFollowersArc(): void {
  useMeta.getState().recordLore('arc-bot-followers', 'Bot Followers Audit');
  useCinematic.getState().fire({
    id: `bot-followers-${Date.now()}`,
    type: 'breaking',
    headline: '87% OF YOUR FOLLOWERS ARE BOTS.',
    subhead: 'a New Yorker reporter just ran an audit. story drops at 6pm. PR flack on line 1. lawyer on line 2. wife on line 3.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `bots-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `bots-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: usePhone.getState().contacts['pr-flack'] ? 'pr-flack' : 'broker',
        fromNpc: usePhone.getState().contacts['pr-flack'] ? 'pr-flack' : 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `bot audit: decision ${i + 1}/3` });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'a new analysis of a "well-known retail trader\'s" social presence found that 87% of their followers were bots. trader\'s response: "that\'s a flattering ratio." analysts: also a flattering response.',
      likes: 14000, reposts: 4400,
    });
  }, 5_000 + BEATS.length * 30_000 + 5_000);
}
